import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {
	LinearProgress,
	styled,
	Avatar,
	Box,
	Card,
	CardHeader,
	CardContent,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Typography,
	ListItemButton,
} from '@mui/material';

// Dashboarders Team Images
import BrianDavenhallIcon from '../contact/Dashboarders/brian_davenhall_jacobs_com_LThumb.jpg'
import JoshFuocoIcon from '../contact/Dashboarders/josh_fuoco_jacobs_com_LThumb.jpg'
import MarkKramerIcon from '../contact/Dashboarders/mark_kramer_jacobs_com_LThumb.jpg'
import PankajPatelIcon from '../contact/Dashboarders/pankaj_patel10_jacobs_com_LThumb.jpg'
import ShellySipesIcon from '../contact/Dashboarders/shelly_sipes_jacobs_com_LThumb.jpg'
import TatAuIIcon from '../contact/Dashboarders/tat_au_jacobs_com_LThumb.jpg'
import TylerHuehmerIcon from '../contact/Dashboarders/tyler_huehmer_jacobs_com_LThumb.jpg'

// Material-UI Icons: Data Loaders
import HomeIcon from '@mui/icons-material/Home'; // Home
import ContactMailIcon from '@mui/icons-material/ContactMail'; // Home Contact
import WallpaperIcon from '@mui/icons-material/Wallpaper'; // Home Image
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'; // Image

import BorderClearIcon from '@mui/icons-material/BorderClear'; // Magic Matrix Position
import AppsIcon from '@mui/icons-material/Apps'; // Magic Matrix Project
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'; // Magic Matrix Question

import TrendingUpIcon from '@mui/icons-material/TrendingUp'; // Measures & Trend
import PostAddIcon from '@mui/icons-material/PostAdd'; // Metadata

import QueueIcon from '@mui/icons-material/Queue'; // Project
import ExtensionIcon from '@mui/icons-material/Extension'; // Project Extension
import StarsIcon from '@mui/icons-material/Stars'; // Project Note
import NotedAddIcon from '@mui/icons-material/NoteAdd'; // Project Status

import WarningIcon from '@mui/icons-material/Warning'; // Cost Report, Cost Report Trend, Risk, Risk Actual Cost, & Risk Mitigation Actions
import HealingIcon from '@mui/icons-material/Healing'; // SHE

import GroupIcon from '@mui/icons-material/Group'; // Staffing

import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'; // Task
import CategoryIcon from '@mui/icons-material/Category'; // Task Category
import PeopleIcon from '@mui/icons-material/People'; // Task Resource

import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Unknown - default

// Material-UI Icons
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

// Home: Contacts
import FaceIcon from '@mui/icons-material/Face';
import PersonIcon from '@mui/icons-material/Person';

// Home: Links
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link'; // Home Link data loader too

// Home: Links - SharePoint
import BookIcon from '@mui/icons-material/Book'; // SharePoint document library
import DescriptionIcon from '@mui/icons-material/Description';
import ListIcon from '@mui/icons-material/List'; // SharePoint list

// Custom Utilities
import {
    getFieldFormatters
} from '../../../../utilities/DataSourceUtilities';

import {
    formatValue,
    formatTypographyColor
} from '../../../../utilities/FormatUtilities';

import useDataSource from '../../../../hooks/useDataSource';
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {useDispatch, useSelector} from "react-redux";
import {findComponent} from "../../../../redux/page/pageActions";
import {dataAddFilter, dataRemoveFilter, filterUpdateConditionAction} from "../../../../redux/data/dataActions";
import ClearFilterBtn from "../../../application/ClearFilterBtn";
import * as DataUtilities from "../../../../utilities/DataUtilities";

const PREFIX = 'SmartList';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    cardHeader: `${PREFIX}-cardHeader`,
    list: `${PREFIX}-list`,
    listItemText: `${PREFIX}-listItemText`
};

const StyledBox = styled(Box)((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.card}`]: {
        height: '100%',
        boxShadow: 'none'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    },

    [`& .${classes.list}`]: {
        width: '100%'
    },

    [`& .${classes.listItemText}`]: {
        marginRight: '6px'
    }
}));

export default function SmartList({id, configuration, xId, onLoading, onLoaded}) {

	const dispatch = useDispatch();

    const { t, i18n } = useTranslation();

	//     configuration: {
	//          name: {
	//              title:
	//              subtitle:
	//          },
	//          dataSource: {
	//              name: <datasource>
	//              fields: {
	//                  primaryText: ARRAY; <primary text field(s) name from datasource>
	//                  secondaryText: ARRAY; <secondary text field(s) name from datasource>
	//                  icon: <icon name field name from datasource>; currently Material UI icon (but could be image)
	//                  iconColor: <icon color field name from datasource>; Hex code used for avatar backgroundColor style and/or icon color (but icon color isn't being used)
	//                  link: <link field name from datasource>
	//              },
	//              templates: {
	//                  primaryText: template from primary text fields
	//                  secondaryText: template from secondary text fields
	//              }
	//          },
	//          element: {
	//              card: {
	//                  boxShadow:
	//                  backgroundColor:
	//              }
	//              primaryText: {
	//                  color: initial | inherit | primary | secondary | textPrimary | textSecondary | error | HEX code
	//                  variant: h1 | h2 | h3 | h4 | h5 | h6 | subtitle1 | subtitle2 | body1 | body2 | caption | button | overline | srOnly | inherit
	//              }
	//              secondaryText: {
	//                  color: initial | inherit | primary | secondary | textPrimary | textSecondary | error | HEX code
	//                  variant: h1 | h2 | h3 | h4 | h5 | h6 | subtitle1 | subtitle2 | body1 | body2 | caption | button | overline | srOnly | inherit
	//              }
	//              listItem: {
	//                  isButton: true | false
	//                  isDense: true | false
	//                  isDivided: true | false
	//              }
	//              icon: {
	//                  type: avatar | icononly | none
	//                  fontSize: inherit | default | small | large
	//                  avatarVariant: circular | rounded | square
	//              }
	//              link: {
	//                  newWindow: true | false
	//                  protocol: file | ftp | mailto | tel (not using; getting from datasource formatter)
	//              }
	//          }
	//      }

	// App State
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const dataDate = useSelector(state => state.data.dataDate);
	const filters = useSelector(state => state.data.filters);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	const [sourceData, setSourceData] = useState([]);
	const [sliceValue, setSliceValue] = useState(-2112);
	const [listData, setListData] = useState([]);
	const [primaryTextTypographyColor, setPrimaryTextTypographyColor] = useState({});
	const [secondaryTextTypographyColor, setSecondaryTextTypographyColor] = useState({});
	const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

    // Misc
    // -- Misc: List
    const listPaddingDisabled = true;
    const listItemGuttersDisabled = true;
    // -- Misc: Icon/Avatar
    const defaultIconColor = 'rgba(0, 0, 0, 0.54)';

    // NAME
    // DATASOURCE
    // -- DATASOURCE: Name
    const dataSourceName = compConfig?.dataSource?.name ? compConfig?.dataSource?.name : '';
	const dataSourceCondition = compConfig.dataSource?.condition ? compConfig.dataSource.condition : null;
    // -- DATASOURCE: Fields
    const primaryTextFields = compConfig?.dataSource?.fields?.primaryText ? compConfig?.dataSource?.fields?.primaryText : [];
    const primaryTextFieldsTemplate = compConfig.dataSource.templates.primaryText ? compConfig.dataSource.templates.primaryText : '';
    const secondaryTextFields = compConfig?.dataSource?.fields?.secondaryText ? compConfig?.dataSource?.fields?.secondaryText : [];
    const secondaryTextFieldsTemplate = compConfig.dataSource.templates.secondaryText ? compConfig.dataSource.templates.secondaryText : '';
    const iconField = compConfig?.dataSource?.fields?.icon ? compConfig?.dataSource?.fields?.icon : '';
    const iconColorField = compConfig?.dataSource?.fields?.iconColor ? compConfig?.dataSource?.fields?.iconColor : '';
    const linkField = compConfig?.dataSource?.fields?.link ? compConfig?.dataSource?.fields?.link : '';
	const xFilter = compConfig?.xFilter !== undefined ? compConfig.xFilter : null;
    // ELEMENT
    // -- CARD Styles
    const styles = compConfig?.element?.styles;
    // -- ELEMENT: Primary Text
    const primaryTextColor = compConfig?.element?.primaryText?.color ? compConfig?.element?.primaryText?.color : 'textPrimary';
    const primaryTextVariant = compConfig?.element?.primaryText?.variant ? compConfig?.element?.primaryText?.variant : 'body1';
    // -- ELEMENT: Secondary Text
    const secondaryTextColor = compConfig?.element?.secondaryText?.color ? compConfig?.element?.secondaryText?.color : 'textSecondary';
    const secondaryTextVariant = compConfig?.element?.secondaryText?.variant ? compConfig?.element?.secondaryText?.variant : 'body2';
    // -- ELEMENT: List Item
    let listItemIsButton = compConfig?.element?.listItem?.isButton !== undefined ? compConfig?.element?.listItem?.isButton : false;
	const listItemIsFilter = compConfig?.element?.listItem?.isFilter !== undefined ? ((compConfig.element.listItem.isFilter && xFilter.enabled) ? true : false ) : false;
	if (listItemIsFilter === true) {
		listItemIsButton = false;
	}
    const listItemIsDense = compConfig?.element?.listItem?.isDense !== undefined ? compConfig?.element?.listItem?.isDense : false;
    const listItemIsDivided = compConfig?.element?.listItem?.isDivided !== undefined ? compConfig?.element?.listItem?.isDivided : false;
    // -- ELEMENT: Icon
    const iconType = compConfig?.element?.icon?.type ? compConfig?.element?.icon?.type : 'none';
    const iconFontSize = compConfig?.element?.icon?.fontSize ? compConfig?.element?.icon?.fontSize : 'medium';
    const avatarVariant = compConfig?.element?.icon?.avatarVariant ? compConfig?.element?.icon?.avatarVariant : 'circle';
    // -- ELEMENT: Link
    const linkTarget = compConfig?.element?.link?.newWindow === true ? '_blank' : '';
    // const linkProtocol = compConfig?.element?.link?.protocol ? compConfig?.element?.link?.protocol : '';

	const dataSource = useDataSource(dataSourceName);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		// console.log(compConfig);
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	// Titles
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// ResizeCard
	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

    // Data
    useEffect(() => {

        if (dataSource.error) {
            // console.log('-----> smartlist:useEffect(): dataSource -- ERROR');
            // TODO: Error component

        } else if (dataSource.data && dataSource.fetched) {
            // console.log('-----> smartlist:useEffect(): dataSource -- FETCHED DATA');

			if (dataSource.includeBaselineResults && dataSource.baselines.length > 0) {
				// Find my baseline
				const baseline = dataSource.baselines.find(d => {
					return d.id === id
				});

				// console.log('------------BASELINE------------->', baseline);
				if (baseline) {
					let useBaseline = true;

					// If there is a baseline then we use
					// that value IF we can highlight
					if (sliceValue !== -2112) {

						if (!baseline.result.find(d => {
								if (d[xFilter.field] && sliceValue) {
									return d[xFilter.field].toString().toLowerCase() === sliceValue.toString().toLowerCase();
								} else {
									return false;
								}
							}
						)
						) {
							useBaseline = false;
						}
					}

					// console.log('------------------USE BASELINE---------------->', useBaseline);
					if (useBaseline) {
						setSourceData(dataSourceCondition ? filterDataSource(baseline.result) : baseline.result);
					}
				} else {
					setSourceData(dataSourceCondition ? filterDataSource(dataSource.data) : dataSource.data);
				}
			} else {
				setSourceData(dataSourceCondition ? filterDataSource(dataSource.data) : dataSource.data);
			}

			// Rehydrate slice
			const componentFilters = filters.find(f => {
				return f.id === id && f.field === xFilter.field
			});

			if (componentFilters) {
				// TODO: Add utility or store in state since this
				// only assumes single condition - for now we'll use
				// local context however sharing across components
				// TBD will share same f.id
				// HACK:
				let condition = componentFilters.conditions[0];

				if (condition.indexOf("eq '") === 0) {
					// String eq
					condition = condition.substr(condition.indexOf("eq '") + 4);
					condition = condition.substr(0, condition.length - 1);

					setSliceValue(condition);
				} else if (condition.indexOf("eq ") === 0) {
					// Numeric eq
					condition = condition.substr(condition.indexOf("eq ") + 3);

					setSliceValue(condition);
				} else if (condition.indexOf("like '") === 0) {
					// String like
					condition = condition.substr(condition.indexOf("like '") + 6);
					condition = condition.substr(0, condition.length - 1);

					if (condition.length > 2) {
						if (condition.substr(0, 1) === '%') {
							condition = condition.substr(1);
						}
						if (condition.substr(condition.length -1) === '%') {
							condition = condition.substr(0, condition.length -1);
						}
					}

					setSliceValue(condition);
				} else {
					console.log("**WARN** Smart List '{0}' was not a recognized filter condition".format(condition));
				}
			}

        } else {
            // console.log('-----> smartlist:useEffect(): dataSource -- OTHER');
            // TODO: Error component
        }

    }, [dataSource.data]);

	useEffect(() => {
		if (sliceValue && sourceData.length > 0) {
			const data = [];


			// Get formatters from datasource
			// const primaryTextFormatters = getFieldFormatters(dataSource, primaryTextFields); // used when only 1 field
			// const secondaryTextFormatters = getFieldFormatters(dataSource, secondaryTextFields); // used when only 1 field
			const linkFormatters = getFieldFormatters(dataSource, linkField);

			for (let i = 0; i < sourceData.length; i++) {
				data.push({
					identifier: sourceData[i][xFilter.field] !== undefined ? sourceData[i][xFilter.field] : i,
					primaryText: getFormattedText(dataSource, sourceData[i], primaryTextFields, primaryTextFieldsTemplate),
					// primaryText: primaryTextFields ? formatValue(sourceData[i][primaryTextFields], primaryTextFormatters, null) : null,
					secondaryText: getFormattedText(dataSource, sourceData[i], secondaryTextFields, secondaryTextFieldsTemplate),
					// secondaryText: secondaryTextFields ? formatValue(sourceData[i][secondaryTextFields], secondaryTextFormatters, null) : null,
					// icon: iconField ? <Person /> : <Person />,
					icon: iconField ? getMaterialUIIcon(sourceData[i][iconField], iconType, avatarVariant, iconColorField ? sourceData[i][iconColorField] : defaultIconColor) : null,
					// icon: iconField ? getMaterialUIIcon(sourceData[i][iconField]) : getMaterialUIIcon(defaultIcon, iconType, avatarVariant, iconColorField ? sourceData[i][iconColorField] : defaultIconColor),
					// iconColor: iconColorField ? sourceData[i][iconColorField] : defaultIconColor,
					// avatarBackgroundColor: iconColorField ? sourceData[i][iconColorField] : defaultAvatarBackgroundColor,
					link: linkField ? formatValue(sourceData[i][linkField], linkFormatters, null) : '',
					backgroundColor: sliceValue !== -2112 ? (sliceValue.toString() === sourceData[i][xFilter.field].toString() ? 'transparent' : '#D3D3D3') : 'transparent'
				});
			}

			// Set component state
			setPrimaryTextTypographyColor(formatTypographyColor(primaryTextColor));
			setSecondaryTextTypographyColor(formatTypographyColor(secondaryTextColor));
			setListData(data);
		}
	}, [sourceData, sliceValue]);

	useEffect(() => {
		const componentFilters = filters.find(f => {
			return f.id === id && f.field === xFilter.field
		});

		if (!componentFilters && sliceValue !== -2112) {
			setSliceValue(-2112);
		}
	}, [filters]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'SmartList Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'list'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

	const filterDataSource = (dataSet) => {
		let filteredData;
		let data;
		const conditionValue = isNaN(dataSourceCondition.conditionValue) ? dataSourceCondition.conditionValue.toLowerCase() : dataSourceCondition.conditionValue;

		filteredData = DataUtilities.filterDataSet(dataSourceCondition.conditionType, dataSourceCondition.conditionOperator, dataSourceCondition.conditionField, conditionValue === 'datadate' ? dataDate : conditionValue, dataSet, null);
		if (Array.isArray(filteredData)) {
			data = filteredData;
		} else {
			data = [filteredData];
		}

		return data;
	}

    const getFormattedText = (dataSource, item, fields, template) => {

        // console.log('> SmartList: getFormattedText()');
        // console.log('  Datasource: ', dataSource);
        // console.log('  Item: ', item);
        // console.log('  Fields: ', fields);

        let formatters = [];
        let value = '';

        for (let i = 0; i < fields.length; i++) {

            // console.log('  - Field: ', fields[i]);

            formatters = getFieldFormatters(dataSource, fields[i]);
            // console.log('  - All Formatters: ', formatters);

            // Remove style formatters for now
            for (let j = 0; j < formatters.length; j++) {
                if (formatters[j].type.toLowerCase() === 'style') {
                    formatters.splice(formatters[j]);
                }
            }
            // console.log('  - All Non-Style Formatters: ', formatters);

            // if (fields.length > 1) {
            //     value = formatValue(item[fields[i]], formatters, null);
            // } else {
            //
            // }
            value = formatValue(item[fields[i]], formatters, null);
            // console.log('  - Value: ', value);

            template = template.replace('{' + i.toString() + '}', value);
            // console.log('  - Template: ', template);
        }

        // console.log('  - Template 2: ', template);
        return fields.length > 0 ? template : null;
    }

    const getMaterialUIIcon = (icon, iconType, avatarVariant, iconColor) => {

        // console.log('> SmartList: getMaterialUIIcon()');
        // console.log('  Icon: {0}'.format(icon));
        // console.log('  Icon Type: {0}'.format(iconType));
        // console.log('  Avatar Variant: {0}'.format(avatarVariant));
        // console.log('  Icon Color: {0}'.format(iconColor));

        switch(icon.toLowerCase()) {
            case 'book':
                if (iconType === 'icononly') {
                    return <BookIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><BookIcon /></Avatar>;
                }
            case 'document':
                if (iconType === 'icononly') {
                    return <DescriptionIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><DescriptionIcon /></Avatar>;
                }
            case 'face':
                if (iconType === 'icononly') {
                    return <FaceIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><FaceIcon /></Avatar>;
                }
            case 'person':
                if (iconType === 'icononly') {
                    return <PersonIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><PersonIcon /></Avatar>;
                }
            case 'language':
                if (iconType === 'icononly') {
                    return <LanguageIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><LanguageIcon /></Avatar>;
                }
            case 'link':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><LinkIcon /></Avatar>;
                }
            case 'list':
                if (iconType === 'icononly') {
                    return <ListIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><ListIcon /></Avatar>;
                }
            case 'amanda.skewis':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Amanda Skewis'} src={AmandaSkewisIcon} />;
                }
            case 'debi.meling':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Debi Meling'} src={DebiMelingIcon} />;
                }
            case 'derick.miller':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Derick Miller'} src={DerickMillerIcon} />;
                }
            case 'jeff.sprock':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Jeff Sprock'} src={JeffSprockIcon} />;
                }
            case 'jennifer.duray':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Jennifer Duray'} src={JenniferDurayIcon} />;
                }
            case 'louis.engels':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Louis Engels'} src={LouisEngelsIcon} />;
                }
            case 'scott.emerick':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Scott Emerick'} src={ScottEmerickIcon} />;
                }
            case 'brian.davenhall':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Brian Davenhall'} src={BrianDavenhallIcon} />;
                }
            case 'josh.fuoco':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Josh Fuoco'} src={JoshFuocoIcon} />;
                }
            case 'mark.kramer':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Mark Kramer'} src={MarkKramerIcon} />;
                }
            case 'pankaj.patel':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Pankaj Patel'} src={PankajPatelIcon} />;
                }
            case 'shelly.sipes':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Shelly Sipes'} src={ShellySipesIcon} />;
                }
            case 'tat.au':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Tat Au'} src={TatAuIIcon} />;
                }
            case 'tyler.huehmer':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} alt={'Tyler Huehmer'} src={TylerHuehmerIcon} />;
                }
            case 'costreport':
                if (iconType === 'icononly') {
                    return <WarningIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><WarningIcon /></Avatar>;
                }
            case 'costreporttrend':
                if (iconType === 'icononly') {
                    return <WarningIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><WarningIcon /></Avatar>;
                }
            case 'home':
                if (iconType === 'icononly') {
                    return <HomeIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><HomeIcon /></Avatar>;
                }
            case 'homecontact':
                if (iconType === 'icononly') {
                    return <ContactMailIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><ContactMailIcon /></Avatar>;
                }
            case 'homeimage':
                if (iconType === 'icononly') {
                    return <WallpaperIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><WallpaperIcon /></Avatar>;
                }
            case 'homelink':
                if (iconType === 'icononly') {
                    return <LinkIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><LinkIcon /></Avatar>;
                }
            case 'image':
                if (iconType === 'icononly') {
                    return <AddPhotoAlternateIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><AddPhotoAlternateIcon /></Avatar>;
                }
            case 'magicmatrixposition':
                if (iconType === 'icononly') {
                    return <BorderClearIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><BorderClearIcon /></Avatar>;
                }
            case 'magicmatrixproject':
                if (iconType === 'icononly') {
                    return <AppsIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><AppsIcon /></Avatar>;
                }
            case 'magicmatrixquestion':
                if (iconType === 'icononly') {
                    return <QuestionAnswerIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><QuestionAnswerIcon /></Avatar>;
                }
            case 'measures':
                if (iconType === 'icononly') {
                    return <TrendingUpIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><TrendingUpIcon /></Avatar>;
                }
            case 'metadata':
                if (iconType === 'icononly') {
                    return <PostAddIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><PostAddIcon /></Avatar>;
                }
            case 'project':
                if (iconType === 'icononly') {
                    return <QueueIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><QueueIcon /></Avatar>;
                }
            case 'projectextension':
                if (iconType === 'icononly') {
                    return <ExtensionIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><ExtensionIcon /></Avatar>;
                }
            case 'projectnote':
                if (iconType === 'icononly') {
                    return <StarsIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><StarsIcon /></Avatar>;
                }
            case 'projectstatus':
                if (iconType === 'icononly') {
                    return <NotedAddIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><NotedAddIcon /></Avatar>;
                }
            case 'risk':
                if (iconType === 'icononly') {
                    return <WarningIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><WarningIcon /></Avatar>;
                }
            case 'riskactualcost':
                if (iconType === 'icononly') {
                    return <WarningIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><WarningIcon /></Avatar>;
                }
            case 'riskmitigationactions':
                if (iconType === 'icononly') {
                    return <WarningIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><WarningIcon /></Avatar>;
                }
            case 'hse':
                if (iconType === 'icononly') {
                    return <HealingIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><HealingIcon /></Avatar>;
                }
            case 'staffing':
                if (iconType === 'icononly') {
                    return <GroupIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><GroupIcon /></Avatar>;
                }
            case 'taskcategory':
                if (iconType === 'icononly') {
                    return <CategoryIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><CategoryIcon /></Avatar>;
                }
            case 'tasklog':
                if (iconType === 'icononly') {
                    return <AssignmentTurnedInIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><AssignmentTurnedInIcon /></Avatar>;
                }
            case 'taskresource':
                if (iconType === 'icononly') {
                    return <PeopleIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><PeopleIcon /></Avatar>;
                }
            case 'trend':
                if (iconType === 'icononly') {
                    return <TrendingUpIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><TrendingUpIcon /></Avatar>;
                }
            default:
                if (iconType === 'icononly') {
                    return <HelpOutlineIcon fontSize={iconFontSize} style={{color: iconColor}}/>
                } else {
                    return <Avatar variant={avatarVariant} style={{backgroundColor: iconColor}}><HelpOutlineIcon/></Avatar>;
                }
        }
    };

    function ListItemLink(props) {
        return <ListItemButton component='a' {...props} />;
    }

	const handleFilter = (value) => {
		let	filterFieldToAdd = xFilter.field;
		let	filterValueToAdd = value;

		if (sliceValue.toString().toLowerCase() === filterValueToAdd.toString().toLowerCase()) {
			dispatch(dataRemoveFilter(id));
		}
		else {
			setSliceValue(filterValueToAdd);
			if (xFilter.operator.toLowerCase() === 'like') {
				dispatch(dataAddFilter(id, filterFieldToAdd, " like '%{0}%'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
			} else {
				dispatch(dataAddFilter(id, filterFieldToAdd, " eq '{0}'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
			}
		}
	}

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {
			...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '8px'
		});

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <StyledBox className={classes.root}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <Box className={classes.linearProgress}></Box>
            }
            {listData &&
            <Card className={classes.card} style={{...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, null)}
							className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{variant: 'subtitle2', color: 'textPrimary'}}
                            title={t(title)}
                            subheaderTypographyProps={{variant: 'body2', color: 'textSecondary'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    {
                        listData.map( (row, index) => (
                            <List key={index} className={classes.list} disablePadding={listPaddingDisabled}>
                                {!listItemIsButton && !listItemIsFilter &&
                                <ListItem key={row.id}
                                          dense={listItemIsDense}
                                          disableGutters={listItemGuttersDisabled}
                                          divider={listItemIsDivided}
                                >
                                    {iconType !== 'none' &&
                                    <ListItemAvatar>
                                        {row.icon}
                                    </ListItemAvatar>
                                    }
                                    {(!primaryTextFields && secondaryTextFields) &&
                                    <ListItemText className={classes.listItemText}
                                                  secondary={<Typography color={secondaryTextTypographyColor.component}
                                                                         variant={secondaryTextVariant}
                                                                         style={{color: secondaryTextTypographyColor.style}}
                                                                         dangerouslySetInnerHTML={{__html: (row.secondaryText)}}>
                                                  </Typography>}>
                                    </ListItemText>
                                    }
                                    {(primaryTextFields && !secondaryTextFields) &&
                                    <ListItemText className={classes.listItemText}
                                                  primary={<Typography color={primaryTextTypographyColor.component}
                                                                       variant={primaryTextVariant}
                                                                       style={{color: primaryTextTypographyColor.style}}
                                                                       dangerouslySetInnerHTML={{__html: (row.primaryText)}}>
                                                  </Typography>}>
                                    </ListItemText>
                                    }
                                    {(primaryTextFields && secondaryTextFields) &&
                                    <ListItemText className={classes.listItemText}
                                                  primary={<Typography color={primaryTextTypographyColor.component}
                                                                       variant={primaryTextVariant}
                                                                       style={{color: primaryTextTypographyColor.style}}
                                                                       dangerouslySetInnerHTML={{__html: (row.primaryText)}}>
                                                  </Typography>}
                                                  secondary={<Typography color={secondaryTextTypographyColor.component}
                                                                         variant={secondaryTextVariant}
                                                                         style={{color: secondaryTextTypographyColor.style}}
                                                                         dangerouslySetInnerHTML={{__html: (row.secondaryText)}}>
                                                  </Typography>}>
                                    </ListItemText>
                                    }
                                </ListItem>
                                }
								{listItemIsFilter &&
									<ListItem button key={row.id}
											  dense={listItemIsDense}
											  disableGutters={listItemGuttersDisabled}
											  divider={listItemIsDivided}
											  onClick={() => handleFilter(row.identifier)}
											  style={{backgroundColor: row.backgroundColor}}
									>
										{iconType !== 'none' &&
											<ListItemAvatar>
												{row.icon}
											</ListItemAvatar>
										}
										{(!primaryTextFields && secondaryTextFields) &&
											<ListItemText className={classes.listItemText}
														  secondary={<Typography color={secondaryTextTypographyColor.component}
																				 variant={secondaryTextVariant}
																				 style={{color: secondaryTextTypographyColor.style}}
																				 dangerouslySetInnerHTML={{__html: (row.secondaryText)}}>
														  </Typography>}>
											</ListItemText>
										}
										{(primaryTextFields && !secondaryTextFields) &&
											<ListItemText className={classes.listItemText}
														  primary={<Typography color={primaryTextTypographyColor.component}
																			   variant={primaryTextVariant}
																			   style={{color: primaryTextTypographyColor.style}}
																			   dangerouslySetInnerHTML={{__html: (row.primaryText)}}>
														  </Typography>}>
											</ListItemText>
										}
										{(primaryTextFields && secondaryTextFields) &&
											<ListItemText className={classes.listItemText}
														  primary={<Typography color={primaryTextTypographyColor.component}
																			   variant={primaryTextVariant}
																			   style={{color: primaryTextTypographyColor.style}}
																			   dangerouslySetInnerHTML={{__html: (row.primaryText)}}>
														  </Typography>}
														  secondary={<Typography color={secondaryTextTypographyColor.component}
																				 variant={secondaryTextVariant}
																				 style={{color: secondaryTextTypographyColor.style}}
																				 dangerouslySetInnerHTML={{__html: (row.secondaryText)}}>
														  </Typography>}>
											</ListItemText>
										}
									</ListItem>
								}
                                {listItemIsButton &&
                                <ListItemLink key={row.id}
                                              dense={listItemIsDense}
                                              disableGutters={listItemGuttersDisabled}
                                              divider={listItemIsDivided}
                                              target={linkTarget}
                                              href={row.link}
                                >
                                    {iconType !== 'none' &&
                                    <ListItemAvatar>
                                        {row.icon}
                                    </ListItemAvatar>
                                    }
                                    {(!primaryTextFields && secondaryTextFields) &&
                                    <ListItemText className={classes.listItemText}
                                                  secondary={<Typography color={secondaryTextTypographyColor.component}
                                                                         variant={secondaryTextVariant}
                                                                         style={{color: secondaryTextTypographyColor.style}}
                                                                         dangerouslySetInnerHTML={{__html: (row.secondaryText)}}>
                                                  </Typography>}>
                                    </ListItemText>
                                    }
                                    {(primaryTextFields && !secondaryTextFields) &&
                                    <ListItemText className={classes.listItemText}
                                                  primary={<Typography color={primaryTextTypographyColor.component}
                                                                       variant={primaryTextVariant}
                                                                       style={{color: primaryTextTypographyColor.style}}
                                                                       dangerouslySetInnerHTML={{__html: (row.primaryText)}}>
                                                  </Typography>}>
                                    </ListItemText>
                                    }
                                    {(primaryTextFields && secondaryTextFields) &&
                                    <ListItemText className={classes.listItemText}
                                                  primary={<Typography color={primaryTextTypographyColor.component}
                                                                       variant={primaryTextVariant}
                                                                       style={{color: primaryTextTypographyColor.style}}
                                                                       dangerouslySetInnerHTML={{__html: (row.primaryText)}}>
                                                  </Typography>}
                                                  secondary={<Typography color={secondaryTextTypographyColor.component}
                                                                         variant={secondaryTextVariant}
                                                                         style={{color: secondaryTextTypographyColor.style}}
                                                                         dangerouslySetInnerHTML={{__html: (row.secondaryText)}}>
                                                  </Typography>}>
                                    </ListItemText>
                                    }
                                </ListItemLink>
                                }
                            </List>
                        ))
                    }
                </CardContent>
            </Card>
            }
        </StyledBox>
    );
}
